import { memo } from 'react'

const H = ({ children }: { children: string }) => (
  <h3 className="mt-4 text-sm font-semibold text-white first:mt-0">{children}</h3>
)
const P = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <p className={`text-[12px] leading-relaxed text-[#cbd5e1] ${className}`.trim()}>{children}</p>
)
const UL = ({ children }: { children: React.ReactNode }) => (
  <ul className="ml-4 list-disc space-y-1 text-[12px] leading-relaxed text-[#cbd5e1]">{children}</ul>
)
const Meta = ({ children }: { children: string }) => (
  <div className="text-[11px] text-[#64748b]">{children}</div>
)

export const TosContent = memo(function TosContent() {
  return (
    <>
      <Meta>Version 1.0 · Effective date: 2026-04-23</Meta>
      <P>
        By using PolyStrike Visual (the &ldquo;Service&rdquo;, the &ldquo;App&rdquo;) you agree to these Terms.
        If you do not agree, do not use the Service.
      </P>

      <H>1. What the Service is</H>
      <P>
        PolyStrike Visual is a <strong>user interface</strong> (UI) to public Polymarket
        protocols (CLOB, CTF, Builder Relayer). The Service:
      </P>
      <UL>
        <li>Displays public market data (prices, order book, positions).</li>
        <li>Lets you place limit YES / NO orders via Polymarket CLOB.</li>
        <li>Provides gasless winnings redeem via Polymarket Builder Relayer.</li>
        <li>Stores the minimum necessary data on the server (see Privacy Policy).</li>
      </UL>
      <P>The Service is <strong>not</strong>:</P>
      <UL>
        <li>a broker, exchange, or custodian;</li>
        <li>an investment advisor or financial intermediary;</li>
        <li>a guarantor of order execution or Polymarket availability;</li>
        <li>a counterparty to trades between users.</li>
      </UL>

      <H>2. Non-custodial</H>
      <P>
        Your PM wallet private key <strong>never leaves your browser</strong>. It is encrypted
        with your PIN (AES-256-GCM + PBKDF2-SHA256, 200,000 iterations) and stored in
        localStorage.
      </P>
      <UL>
        <li>The Service <strong>cannot sign a transaction on your behalf</strong> without your PIN.</li>
        <li>The Service <strong>cannot recover</strong> your private key or funds if you forget the PIN or lose the device.</li>
        <li>You bear full responsibility for the security of your PIN, device, and backups of your private key.</li>
      </UL>

      <H>3. Subscription and payment</H>
      <P>The Service is provided on a subscription basis:</P>
      <UL>
        <li><strong>Trial</strong> — 7 days free, full functionality, once per PM wallet.</li>
        <li><strong>Active subscription</strong> — 8.5 USDC for 30 days, paid in USDC on Polygon; the transfer to the treasury address is signed by your key in the browser.</li>
      </UL>
      <P>
        The subscription <strong>does not auto-renew</strong>. When it expires, you do not lose
        access to your funds on Polymarket — you can manage them via polymarket.com. Trading
        inside PolyStrike Visual is blocked until renewal.
      </P>

      <H>4. Refunds</H>
      <P>
        <strong>On-chain USDC payment is irreversible.</strong> A refund is technically impossible
        once the funds have reached the treasury and the subscription has been activated.
      </P>
      <P>Exceptions:</P>
      <UL>
        <li>A technical error on the Service side where the subscription was charged but not activated (contact support).</li>
        <li>A major Polymarket CLOB outage on the payment day fully blocking trading for more than 24 hours.</li>
      </UL>
      <P>In all other cases (changed your mind, not using, unsatisfied) — no refund is issued.</P>

      <H>5. Trading risks</H>
      <P>
        Trading predictions on Polymarket carries the <strong>risk of total loss of funds</strong>.
        You confirm that:
      </P>
      <UL>
        <li>You understand how prediction markets and CLOB orders work.</li>
        <li>You assess the risks of each trade yourself.</li>
        <li>You do not rely on the Service&rsquo;s interface as investment advice.</li>
        <li>You accept that prices on Polymarket are volatile, liquidity is limited, and order execution is not guaranteed.</li>
      </UL>

      <H>6. Jurisdiction and availability</H>
      <P>
        Polymarket CLOB blocks trading requests from a number of jurisdictions (US, UK, Germany,
        France, Italy and others). The Service <strong>does not check</strong> user geolocation — you are
        responsible for ensuring your use of the Service complies with the laws of your jurisdiction.
      </P>
      <P>You must not use the Service:</P>
      <UL>
        <li>from jurisdictions where prediction markets are prohibited;</li>
        <li>for purposes that violate KYC/AML obligations;</li>
        <li>via automated bots (except your own tools that do not harm market integrity).</li>
      </UL>

      <H>7. Limitation of liability</H>
      <P>
        The Service is provided &ldquo;as is&rdquo;, without any express or implied warranties. The owner
        of the Service is not liable for:
      </P>
      <UL>
        <li>Losses from user trading decisions.</li>
        <li>Failures, delays, or inaccuracies of data from Polymarket or the blockchain.</li>
        <li>User compromise (private-key leak, phishing, weak PIN).</li>
        <li>Third-party actions (Polymarket, RPC providers, Telegram).</li>
      </UL>
      <P>
        The maximum aggregate liability of the Service to a user is limited to the amount of
        subscription fees paid by that user during the last 12 months.
      </P>

      <H>8. Changes to the Terms</H>
      <P>
        We may update these Terms. Material changes will be posted in the ReadmeDialog inside the
        App. Continued use after notice = agreement.
      </P>

      <H>9. Termination</H>
      <P>
        We may suspend or terminate the Service for a specific user who violates these Terms. The
        user may stop using the Service at any time — funds on Polymarket remain under the
        user&rsquo;s control.
      </P>

      <H>10. Contact</H>
      <P>
        <strong>Support:</strong> support@polystrike.app<br />
        <strong>Telegram:</strong> @PolyStrike_autobot
      </P>
    </>
  )
})

export const PrivacyContent = memo(function PrivacyContent() {
  return (
    <>
      <Meta>Version 1.0 · Date: 2026-04-23</Meta>

      <H>Principle</H>
      <P>
        <strong>Minimum data.</strong> The Service is designed to store as little as possible. Your
        PM wallet private key never reaches the server.
      </P>

      <H>1. What we collect</H>

      <P className="mt-1"><strong>1.1 Locally in the browser (localStorage)</strong></P>
      <UL>
        <li>Encrypted PM wallet private key (AES-256-GCM + PBKDF2-SHA256 200,000 iterations, encryption key = your PIN).</li>
        <li>UI settings (selected crypto, streak counter).</li>
      </UL>
      <P>
        This data is <strong>not transmitted</strong> to the server. Clear it via DevTools →
        Application → Local Storage → Clear, or via &ldquo;Reset wallet&rdquo; in the Wallet dialog.
      </P>

      <P className="mt-1"><strong>1.2 On the server</strong></P>
      <UL>
        <li><code>pm_proxy_address</code> (<code>users</code>) — subscription identification.</li>
        <li><code>tg_id</code> (<code>users</code>) — optional, if signed in via TMA.</li>
        <li><code>trial_ends_at</code> (<code>users</code>) — when the trial ends.</li>
        <li><code>paid_until</code> (<code>users</code>) — subscription paid until.</li>
        <li><code>tx_hash</code>, <code>amount</code>, <code>nonce</code>, <code>block_number</code> (<code>subscription_charges</code>) — payment audit.</li>
      </UL>
      <P>We do <strong>not</strong> store:</P>
      <UL>
        <li>Private keys or their hashes.</li>
        <li>PIN or its hash.</li>
        <li>IP addresses (pino logs may contain them temporarily — rotated every 14 days).</li>
        <li>Email, phone, name, geolocation.</li>
        <li>Details of your Polymarket trades — they live on Polymarket.</li>
      </UL>

      <P className="mt-1"><strong>1.3 Logs</strong></P>
      <UL>
        <li><strong>App (pino):</strong> INFO level; fields with keys/signatures are redacted (regex 0x[a-fA-F0-9]{'{'}64{'}'} → 0xREDACTED).</li>
        <li><strong>nginx access:</strong> standard format, with IP. Retention 14 days via logrotate.</li>
        <li><strong>Sentry (if enabled):</strong> runtime errors with hex64 redaction. No PII.</li>
      </UL>

      <H>2. How we use it</H>
      <UL>
        <li><code>pm_proxy_address</code> → subscription-status check at <code>GET /api/sub/:addr</code>.</li>
        <li><code>subscription_charges</code> → audit, accounting, support (&ldquo;why didn&rsquo;t my payment activate?&rdquo;).</li>
        <li><code>tg_id</code> → optional, to link the TMA session (if you signed in via Telegram Mini App).</li>
      </UL>
      <P>We do <strong>not</strong>:</P>
      <UL>
        <li>Sell data.</li>
        <li>Share with third parties (other than Polymarket, Binance, Sentry — see section 4).</li>
        <li>Profile users or run targeted advertising.</li>
      </UL>

      <H>3. Where it is stored</H>
      <UL>
        <li><strong>Server:</strong> EC2 in eu-south-2 (Spain), SQLite on disk, WAL mode.</li>
        <li><strong>Backups:</strong> nightly encrypted SQLite snapshots to a private S3 bucket within the same AWS account. 30-day retention.</li>
        <li><strong>Retention:</strong> <code>users</code> / <code>subscription_charges</code> are not deleted (needed for audit). If you want deletion — contact support (see section 7).</li>
      </UL>

      <H>4. Who else sees your data</H>
      <UL>
        <li><strong>Polymarket</strong> (gamma / clob / data APIs): your public PM proxy address and your orders — a prediction market is a public protocol; all tx are on-chain.</li>
        <li><strong>Binance</strong> (WebSocket): only your session IP, no signature — source of live price.</li>
        <li><strong>Polygon RPC</strong> (publicnode, llama, drpc): <code>balanceOf</code> and <code>getLogs</code> requests with IP — reading USDC balance and the watcher.</li>
        <li><strong>Polymarket Builder Relayer:</strong> your signed meta-tx — gasless transaction submission.</li>
        <li><strong>Sentry</strong> (if DSN is set): error stack traces with release tag — triage.</li>
        <li><strong>Telegram</strong> (if via TMA): <code>initData</code> (tg_id, username, first_name) — identification in the TMA flow.</li>
      </UL>

      <H>5. Your rights</H>
      <P>
        <strong>5.1 Access / export.</strong> All your data: <code>GET /api/sub/&lt;your pm_proxy_address&gt;</code>{' '}
        and the registry of your <code>subscription_charges</code> are available on request to support.
      </P>
      <P>
        <strong>5.2 Deletion.</strong> Email support — we will delete <code>users</code> and{' '}
        <code>subscription_charges</code> records for your pm_proxy_address within 30 days; note
        you may lose your current subscription in doing so.
      </P>
      <P>
        <strong>5.3 Restriction.</strong> Stop using the Service. Local data is erased via &ldquo;Reset
        wallet&rdquo; in the Wallet dialog. Server-side records remain until manual deletion.
      </P>

      <H>6. Cookies and trackers</H>
      <P>
        The Service <strong>does not use cookies</strong>. There are no analytics (neither Google,
        PostHog nor Mixpanel). The only client-side middleware is Sentry — and only when explicitly
        enabled.
      </P>

      <H>7. Contact</H>
      <P>
        <strong>Support:</strong> support@polystrike.app<br />
        <strong>Data Protection:</strong> privacy@polystrike.app<br />
        <strong>Legal address:</strong> [to be specified after company registration]
      </P>

      <H>8. Policy updates</H>
      <P>
        Material changes are posted in the ReadmeDialog. Continued use = agreement.
      </P>
    </>
  )
})
